import React, { useEffect, useState } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EvaluationCompare } from "@/components/EvaluationCompare";
import { CrossEvalCompare } from "@/components/CrossEvalCompare";
import { useConfirm } from "@/components/ConfirmDialog";
import { EvaluationDialog } from "@/components/EvaluationDialog";

export default function Evaluations() {
  const [students, setStudents] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [evals, setEvals] = useState([]);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const confirm = useConfirm();

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get("/students"), api.get("/evaluations/criteria")]);
        setStudents(s.data);
        setCriteria(c.data.criteria);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const reloadEvals = async () => {
    if (!studentId) { setEvals([]); return; }
    try {
      const { data } = await api.get(`/evaluations/student/${studentId}`);
      setEvals(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { reloadEvals(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [studentId]);

  const del = async (id) => {
    const ok = await confirm({ title: "Excluir avaliação?", confirmText: "Excluir", destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/evaluations/${id}`);
      toast.success("Excluída");
      setEvals((cur) => cur.filter(e => e.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-evaluations-page">
      <PageHeader
        eyebrow="Técnico"
        title="Avaliações"
        subtitle="Registre e acompanhe o desempenho técnico de cada aluno (escala 1-10)."
        mascot={BRAND.MANGUITO_SAQUE}
      />

      <div className="vv-card p-4 mb-4 flex flex-col sm:flex-row gap-3 items-end">
        <div className="flex-1 w-full">
          <Label>Aluno</Label>
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger data-testid="eval-student-select"><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
            <SelectContent>
              {students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} • {s.category}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setDialog({ open: true, editing: null })} disabled={!studentId} className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase" data-testid="new-evaluation-button">
          <Plus className="size-4 mr-2" /> Nova avaliação
        </Button>
      </div>

      {!studentId ? (
        <Tabs defaultValue="cross">
          <TabsList>
            <TabsTrigger value="cross" data-testid="admin-tab-cross-empty">Comparar Atletas</TabsTrigger>
          </TabsList>
          <TabsContent value="cross">
            <CrossEvalCompare students={students} />
          </TabsContent>
          <div className="mt-4">
            <EmptyState title="Selecione um aluno acima" description="Para ver histórico ou comparar avaliações do mesmo aluno, escolha um na lista. Você também pode comparar avaliações entre atletas diferentes na aba acima." mascot={BRAND.MANGUITA} />
          </div>
        </Tabs>
      ) : evals.length === 0 ? (
        <Tabs defaultValue="cross">
          <TabsList>
            <TabsTrigger value="cross" data-testid="admin-tab-cross">Comparar Atletas</TabsTrigger>
          </TabsList>
          <TabsContent value="cross">
            <CrossEvalCompare students={students} />
          </TabsContent>
          <div className="mt-4">
            <EmptyState title="Nenhuma avaliação para este aluno" description="Clique em 'Nova avaliação' para registrar a primeira." />
          </div>
        </Tabs>
      ) : (
        <Tabs defaultValue="history">
          <TabsList>
            <TabsTrigger value="history" data-testid="admin-tab-history">Histórico</TabsTrigger>
            <TabsTrigger value="compare" data-testid="admin-tab-compare">Comparar (mesmo aluno)</TabsTrigger>
            <TabsTrigger value="cross" data-testid="admin-tab-cross">Comparar Atletas</TabsTrigger>
          </TabsList>
          <TabsContent value="history">
            <div className="space-y-3">
              {[...evals].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => (
                <div key={e.id} className="vv-card p-4" data-testid={`eval-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-black text-lg">{e.date}</div>
                      {e.notes && <div className="text-sm text-muted-foreground">{e.notes}</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => setDialog({ open: true, editing: e })} data-testid={`edit-eval-${e.id}`} title="Editar">
                        <Pencil className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(e.id)} data-testid={`delete-eval-${e.id}`} title="Excluir">
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                    {Object.entries(e.scores || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between bg-muted/40 rounded px-2.5 py-1.5 text-sm">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-black text-[hsl(var(--vv-lime))] text-base">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="compare">
            <EvaluationCompare evaluations={evals} />
          </TabsContent>
          <TabsContent value="cross">
            <CrossEvalCompare students={students} />
          </TabsContent>
        </Tabs>
      )}

      <EvaluationDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
        studentId={studentId}
        evaluation={dialog.editing}
        criteria={criteria}
        onSaved={reloadEvals}
      />
    </div>
  );
}
